/**
 * Operator entry guard (Phase I, T-048).
 *
 * Layers a tiny context resolution on top of the Phase C `requireOperator`
 * route guard. Where `portal-guard.ts` has to check subscriber state and
 * onboarding-complete, the operator guard only needs to confirm the role and
 * surface the operator's `auth.users.id` so child routes can attribute
 * writes (e.g., recipient_user_id on operator-targeted notifications) without
 * each one re-reading the session.
 *
 * Outcomes mirror `requireOperator`:
 *   - No session                 -> redirect to /auth/sign-in
 *   - Session but not operator   -> notFound() (surface stays invisible)
 *   - Operator session > 7 days  -> sign out + redirect to /auth/sign-in
 *                                   (ARCH-002 §7)
 *
 * Coordination with Phase C: this guard does NOT modify route-guards.ts. It
 * reuses `requireOperator` as-is and only adds the context-object shape.
 */

import { redirect } from '@tanstack/react-router';
import { getCurrentSession } from './auth.js';
import { requireOperator } from './route-guards.js';

export interface OperatorEntryContext {
  userId: string;
  email: string | null;
}

export async function requireOperatorEntry(opts?: {
  redirectFrom?: string;
}): Promise<OperatorEntryContext> {
  await requireOperator(
    opts?.redirectFrom !== undefined ? { redirectFrom: opts.redirectFrom } : {},
  );

  // requireOperator already guarantees a non-null session + operator role +
  // an unexpired session; the null check below keeps types honest and
  // catches a session that races to log out between the two calls.
  const session = await getCurrentSession();
  if (!session) {
    throw redirect({ to: '/auth/sign-in' });
  }
  return {
    userId: session.user.id,
    email: session.user.email ?? null,
  };
}
